function decodeHtmlEntities(str) {
  if (!str && str !== 0) return str;
  return String(str)
    .replace(/&apos;|&#39;/g, "'")
    .replace(/&quot;/g, '"')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>');
}

function norm(x) {
  return decodeHtmlEntities(x)?.trim?.() ?? '';
}

/* ---------- Normalisasi window.collectionData ---------- */
function normalizeCollectionData() {
  if (!window.collectionData) return;

  for (const [, product] of Object.entries(window.collectionData)) {
    if (!product || !Array.isArray(product.data)) continue;

    if (product.name) product.name = decodeHtmlEntities(product.name);
    if (product.link) product.link = decodeHtmlEntities(product.link);

    product.data.forEach(group => {
      if (group.size) group.size = decodeHtmlEntities(group.size);
      if (group.name) group.name = decodeHtmlEntities(group.name);

      (group.variants || []).forEach(v => {
        if (v.name) v.name = decodeHtmlEntities(v.name);
        if (v.option1) v.option1 = decodeHtmlEntities(v.option1);
        if (v.option2) v.option2 = decodeHtmlEntities(v.option2);
        if (v.option3) v.option3 = decodeHtmlEntities(v.option3);
        if (Array.isArray(v.options)) v.options = v.options.map(decodeHtmlEntities);

        if (!v.variant_metafields) v.variant_metafields = {};
        if (v.variant_metafields['custom.width']) {
          v.variant_metafields['custom.width'] = decodeHtmlEntities(v.variant_metafields['custom.width']);
        }
        if (v.variant_metafields['custom.length']) {
          v.variant_metafields['custom.length'] = decodeHtmlEntities(v.variant_metafields['custom.length']);
        }
      });
    });
  }
}

/* ---------- Ambil nilai facet dari URLSearchParams ---------- */
function getFacetValuesFrom(paramsLike, key) {
  const p = paramsLike instanceof URLSearchParams
    ? paramsLike
    : new URLSearchParams(String(paramsLike || ''));

  const all = p.getAll(key);
  if (all && all.length) return all.map(v => norm(decodeURIComponent(v)));

  const single = p.get(key);
  if (!single) return [];
  return single.split(',').map(v => norm(decodeURIComponent(v)));
}

/* ---------- Recompute filtered + broadcast ke Alpine ---------- */
function recomputeAndBroadcastFiltered(searchParamsOverride = null) {
  normalizeCollectionData();

  const params = searchParamsOverride
    ? (searchParamsOverride instanceof URLSearchParams ? searchParamsOverride : new URLSearchParams(searchParamsOverride))
    : new URLSearchParams(window.location.search);

  const widthVals  = getFacetValuesFrom(params, 'filter.v.m.custom.width');
  const lengthVals = getFacetValuesFrom(params, 'filter.v.m.custom.length');

  const matchesVariant = (variant) => {
    const mf = (variant.variant_metafields || {});
    const w = norm(mf['custom.width']);
    const l = norm(mf['custom.length']);
    const okW = widthVals.length  ? widthVals.includes(w) : true;
    const okL = lengthVals.length ? lengthVals.includes(l) : true;
    return okW && okL;
  };

  const src = window.collectionData || {};
  const filtered = {};

  for (const [pid, product] of Object.entries(src)) {
    if (!product || !Array.isArray(product.data)) continue;

    const newProduct = JSON.parse(JSON.stringify(product));
    newProduct.data = product.data
      .map(group => {
        const g2 = { ...group };
        g2.size = norm(g2.size);
        g2.name = norm(g2.name);
        g2.variants = (group.variants || []).filter(v => matchesVariant(v));
        return g2;
      })
      .filter(g => (g.variants || []).length > 0);

    if (newProduct.data.length > 0) filtered[pid] = newProduct;
  }

  // simpan global jika dibutuhkan oleh UI lain
  window.__filteredCollectionData = filtered;

  // Notifikasi untuk Alpine
  document.dispatchEvent(new CustomEvent('collection:filtered', { detail: { filtered } }));
}

/* ---------- Ambil ulang window.collectionData dari HTML SSR ---------- */
function reEvalCollectionDataFromHTMLString(htmlString) {
  try {
    const doc = new DOMParser().parseFromString(htmlString, 'text/html');
    const scripts = Array.from(doc.querySelectorAll('script'));
    const rx = /window\.collectionData\s*=\s*\{[\s\S]*?\};/m;
    for (const s of scripts) {
      const code = s.textContent || '';
      const m = code.match(rx);
      if (m && m[0]) {
        // set ulang collectionData dari HTML terbaru
        (new Function(m[0]))();
        return;
      }
    }
  } catch (err) {
    console.error('[FacetBridge] Gagal sinkronisasi collectionData:', err);
  }
}

/* ---------- Helper: push URL + recompute instan + fetch ---------- */
function pushAndFetch(qs, event, ctx) {
  const p = new URLSearchParams(qs);
  p.delete('page');                       // reset pagination setiap filter berubah
  const qsNoPage = p.toString();

  FacetFiltersForm.updateURLHash(qsNoPage);     // 1) URL langsung update
  recomputeAndBroadcastFiltered(qsNoPage);      // 2) UI langsung tersaring (tanpa nunggu fetch)
  ctx.onSubmitForm(qsNoPage, event);            // 3) SSR fetch section (grid, facets, count)
}

/* =========================
   Dawn-like Facets bridge
   ========================= */
class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector('form');
    if (facetForm) facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  /* ------ History listener ------ */
  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  /* ------ UI helpers ------ */
  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  /* ------ Core renderer ------ */
  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;

    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll('.facets-container .loading__spinner, facet-filters-form .loading__spinner');

    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));

    const gridContainer = document.getElementById('ProductGridContainer');
    if (gridContainer && gridContainer.querySelector('.collection')) {
      gridContainer.querySelector('.collection').classList.add('loading');
    }
    if (countContainer) countContainer.classList.add('loading');
    if (countContainerDesktop) countContainerDesktop.classList.add('loading');

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;
      const qs = searchParams; // lempar QS aktif untuk recompute di renderer

      if (FacetFiltersForm.filterData.some(filterDataUrl)) {
        FacetFiltersForm.renderSectionFromCache(filterDataUrl, event, qs);
      } else {
        FacetFiltersForm.renderSectionFromFetch(url, event, qs);
      }
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event, qs) {
    fetch(url)
      .then((r) => r.text())
      .then((html) => {
        // 1) sinkronkan data mentah dari HTML SSR → window.collectionData
        reEvalCollectionDataFromHTMLString(html);

        // 2) render sub-bagian SSR
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);

        // 3) recompute client-side berdasarkan QS (agar tidak "balik lagi")
        recomputeAndBroadcastFiltered(qs);

        // 4) sinyal umum
        document.dispatchEvent(new CustomEvent('collection:updated', { detail: { url } }));

        if (typeof initializeScrollAnimationTrigger === 'function') {
          initializeScrollAnimationTrigger(html);
        }
      });
  }

  static renderSectionFromCache(filterDataUrl, event, qs) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;

    // 1) sinkronkan data mentah
    reEvalCollectionDataFromHTMLString(html);

    // 2) render sub-bagian SSR
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);

    // 3) recompute client-side (hindari “balik lagi”)
    recomputeAndBroadcastFiltered(qs);

    // 4) sinyal umum
    document.dispatchEvent(new CustomEvent('collection:updated', { detail: { from: 'cache' } }));

    if (typeof initializeScrollAnimationTrigger === 'function') {
      initializeScrollAnimationTrigger(html);
    }
  }

  static renderProductGridContainer(html) {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const newInner = parsed.getElementById('ProductGridContainer').innerHTML;

    const container = document.getElementById('ProductGridContainer');
    container.innerHTML = newInner;

    container.querySelectorAll('.scroll-trigger').forEach((element) => {
      element.classList.add('scroll-trigger--cancel');
    });

    if (window.Alpine && typeof window.Alpine.initTree === 'function') {
      window.Alpine.initTree(container);
    }
  }

  static renderProductCount(html) {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const countEl = parsed.getElementById('ProductCount');
    const count = countEl ? countEl.innerHTML : '';
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');

    if (container) {
      container.innerHTML = count;
      container.classList.remove('loading');
    }
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }

    const loadingSpinners = document.querySelectorAll('.facets-container .loading__spinner, facet-filters-form .loading__spinner');
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );

    // Hapus facet yang sudah tidak ada
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      if (currentElement) {
        currentElement.innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }
        if (elementToRender.parentElement) {
          document.querySelector(`#${elementToRender.parentElement.id} .js-filter`).before(elementToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter')?.id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);
        const newElementSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
          ? `.mobile-facets__close-button`
          : `.facets__summary`;
        const newElementToActivate = newFacetDetailsElement.querySelector(newElementSelector);

        const isTextInput = event.target.getAttribute('type') === 'text';
        if (newElementToActivate && !isTextInput) newElementToActivate.focus();
      }
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

    const drawer = document.getElementById('FacetFiltersFormMobile');
    if (drawer && drawer.closest('menu-drawer') && typeof drawer.closest('menu-drawer').bindEvents === 'function') {
      drawer.closest('menu-drawer').bindEvents();
    }
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');
    if (sourceSummary && targetSummary) targetSummary.outerHTML = sourceSummary.outerHTML;

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');
    if (sourceHeaderElement && targetHeaderElement) targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }
      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');
    if (sourceFacetsList && targetFacetsList) targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  /* ------ Submit & remove handlers ------ */
  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();

    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    const createQS = (form) => new URLSearchParams(new FormData(form)).toString();

    if (event.srcElement.className === 'mobile-facets__checkbox') {
      const qs = createQS(event.target.closest('form'));
      pushAndFetch(qs, event, this);
      return;
    }

    const forms = [];
    const isMobile = event.target.closest('form')?.id === 'FacetFiltersFormMobile';

    sortFilterForms.forEach((form) => {
      if (!isMobile) {
        if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
          forms.push(createQS(form));
        }
      } else if (form.id === 'FacetFiltersFormMobile') {
        forms.push(createQS(form));
      }
    });

    const qs = forms.join('&');
    pushAndFetch(qs, event, this);
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();

    const qs =
      event.currentTarget.href.indexOf('?') === -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);

    pushAndFetch(qs, event, this);
  }
}

/* ---------- Bootstrap custom element ---------- */
FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

/* ---------- PriceRange & FacetRemove (Dawn default) ---------- */
class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) => {
      element.addEventListener('change', this.onRangeChange.bind(this));
      element.addEventListener('keydown', this.onKeyDown.bind(this));
    });
    this.setMinAndMaxValues();
  }
  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }
  onKeyDown(event) {
    if (event.metaKey) return;
    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }
  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', 0);
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max'));
  }
  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('data-min'));
    const max = Number(input.getAttribute('data-max'));
    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}
customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }
  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}
customElements.define('facet-remove', FacetRemove);
